import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, Package, TrendingUp, DollarSign, 
  Settings, Menu, X, Moon, Sun, BarChart3,
  ShoppingCart, AlertCircle, CreditCard,
  Users, Building2, Crown, Activity
} from 'lucide-react';
import { productsAPI, companiesAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useSelectedCompany } from '../contexts/SelectedCompanyContext';
import { useSidebar } from '../contexts/SidebarContext';
import Logo from './Logo';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedCompanyId, selectCompany, clearSelection } = useSelectedCompany();
  const { isOpen, toggleSidebar } = useSidebar();
  const [lowStockCount, setLowStockCount] = useState(0);
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('darkMode') === 'true'
  );
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Verificar se é master
  const isMaster = user?.email === 'adm@revicont.dev' || 
                   user?.email === 'adm@rescont.dev' || 
                   (user?.role === 'admin' && !user?.companyId);

  // Verificar se é admin de empresa (não master)
  const isCompanyAdmin = user?.role === 'admin' && user?.companyId && !isMaster;

  // Carregar empresas para o master
  useEffect(() => {
    if (isMaster && !selectedCompanyId) {
      loadCompanies();
    }
  }, [isMaster, selectedCompanyId]);

  const loadCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const data = await companiesAPI.getAll();
      setCompanies(data || []);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Verificar produtos com estoque baixo (apenas quando tiver empresa selecionada)
  useEffect(() => {
    if (!isMaster || selectedCompanyId) {
      const checkLowStock = async () => {
        try {
          const products = await productsAPI.getAll();
          if (products && Array.isArray(products)) {
            const count = products.filter(p => p.stock <= p.minStock).length;
            setLowStockCount(count);
          }
        } catch (error) {
          console.error('Erro ao verificar estoque:', error);
          setLowStockCount(0);
        }
      };

      checkLowStock();
      const interval = setInterval(checkLowStock, 60000);
      return () => clearInterval(interval);
    }
  }, [isMaster, selectedCompanyId]);

  // Verificar se é admin (master ou de empresa)
  const isAdmin = user?.role === 'admin';

  // Menu normal (para usuários ou master com empresa selecionada)
  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard', color: 'blue' },
    { icon: Package, label: 'Produtos', path: '/product-management', color: 'green' },
    { icon: ShoppingCart, label: 'Estoque', path: '/stock', color: 'purple' },
    { icon: DollarSign, label: 'Financeiro', path: '/financial-reports', color: 'yellow' },
    { icon: CreditCard, label: 'Pagamentos', path: '/billing', color: 'emerald' },
    ...(isAdmin ? [{ icon: Activity, label: 'Atividades', path: '/activities', color: 'indigo', badge: 'Admin' }] : []),
    ...(isCompanyAdmin ? [{ icon: Crown, label: 'Painel de Controle', path: '/admin-control', color: 'purple', badge: 'Admin' }] : []),
    { icon: Settings, label: 'Configurações', path: '/settings', color: 'gray' },
  ];

  // Menu master (quando master não tem empresa selecionada)
  const masterMenuItems = [
    { 
      icon: Building2, 
      label: 'Empresas', 
      path: '/companies',
      color: 'blue',
      badge: 'Admin'
    },
    { 
      icon: Users, 
      label: 'Usuários', 
      path: '/users',
      color: 'gray',
      badge: 'Admin'
    },
    { 
      icon: CreditCard, 
      label: 'Assinaturas', 
      path: '/subscriptions',
      color: 'gray',
      badge: 'Admin'
    },
    { 
      icon: Settings, 
      label: 'Configurações', 
      path: '/settings',
      color: 'gray',
      badge: 'Admin'
    },
    { 
      icon: Crown, 
      label: 'Painel Master', 
      path: '/super-admin',
      color: 'purple',
      badge: 'Master'
    },
  ];

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode);
    
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Aplicar dark mode no carregamento
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    }
  }, [darkMode]);

  const handleSelectCompany = (companyId) => {
    selectCompany(companyId);
    navigate('/dashboard');
  };

  const handleExitCompany = () => {
    clearSelection();
    // Voltar para dashboard ou settings (o sidebar mostrará menu master automaticamente)
    navigate('/settings');
  };

  // Se for master e não tiver empresa selecionada, mostrar menu master
  const showMasterMenu = isMaster && !selectedCompanyId;
  const currentMenuItems = showMasterMenu ? masterMenuItems : menuItems;

  return (
    <>
      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-screen bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 z-40 flex flex-col ${
        isOpen ? 'w-64' : 'w-20'
      }`}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 h-16">
          {isOpen && (
            <div className="flex items-center gap-2">
              <Logo size="md" />
              <h1 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                EstoquePro
              </h1>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title={isOpen ? 'Recolher menu' : 'Expandir menu'}
          >
            {isOpen ? (
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            ) : (
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            )}
          </button>
        </div>

        {/* Seletor de Empresa (Master sem empresa selecionada) */}
        {showMasterMenu && isOpen && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Selecionar Empresa
            </p>
            <select
              value={selectedCompanyId || ''}
              onChange={(e) => {
                if (e.target.value) {
                  handleSelectCompany(parseInt(e.target.value));
                }
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Selecione uma empresa...</option>
              {loadingCompanies ? (
                <option disabled>Carregando...</option>
              ) : (
                companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))
              )}
            </select>
          </div>
        )}

        {/* Indicador de Empresa Selecionada (Master com empresa) */}
        {isMaster && selectedCompanyId && isOpen && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Empresa Atual
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {companies.find(c => c.id === selectedCompanyId)?.name || 'Carregando...'}
                </p>
              </div>
              <button
                onClick={handleExitCompany}
                className="ml-2 p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Sair da empresa"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Menu Items */}
        <nav className="p-4 space-y-1 overflow-y-auto flex-1" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {currentMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
                            (location.pathname.startsWith(item.path) && item.path !== '/settings');
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all group relative ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                title={!isOpen ? item.label : ''}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${
                  isActive ? 'text-white' : `text-${item.color}-500`
                }`} />
                {isOpen && (
                  <>
                    <span className="font-medium flex-1">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
                
                {/* Indicator Bar */}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Alerta de Estoque Baixo - Só mostra quando há produtos com estoque baixo e não for master sem empresa */}
        {isOpen && lowStockCount > 0 && !showMasterMenu && (
          <div className="mx-4 mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg animate-pulse">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Atenção!
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  {lowStockCount} produto{lowStockCount > 1 ? 's' : ''} com estoque baixo
                </p>
              </div>
            </div>
          </div>
        )}

        {/* P-TechAI Branding + Dark Mode */}
        <div className="mt-auto px-4 pb-4 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
          {/* P-TechAI Branding */}
          {isOpen && (
            <div className="flex items-center gap-3 justify-center">
              <Logo size="md" className="" useImage={true} />
              
              <div className="text-sm">
                <p className="text-gray-500 dark:text-gray-400 leading-tight">Desenvolvido por</p>
                <p className="font-black leading-tight tracking-tight">
                  <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent drop-shadow-sm">P-Tech</span>
                  <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent drop-shadow-sm">AI</span>
                </p>
              </div>
            </div>
          )}
          
          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
              darkMode
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-100'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
          >
            {darkMode ? (
              <>
                <Sun className="w-5 h-5 text-yellow-500" />
                {isOpen && <span className="font-medium">Modo Claro</span>}
              </>
            ) : (
              <>
                <Moon className="w-5 h-5 text-indigo-500" />
                {isOpen && <span className="font-medium">Modo Escuro</span>}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Overlay para mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
}
